import { defaultMarkerColor } from '../styles/js/colors.style'
import extensions from './extensions'

function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | undefined
  return function (this: any, ...args: Parameters<T>) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

interface IColor {
  r: number
  g: number
  b: number
}

const hexToIColor = (hex: string): IColor => {
  hex = hex.replace(/^#/, '')
  let bigint = parseInt(hex, 16)
  let r = (bigint >> 16) & 255
  let g = (bigint >> 8) & 255
  let b = bigint & 255
  return { r: r / 255, g: g / 255, b: b / 255 }
}

const decodeRes = async (response: Response): Promise<any> => {
  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let res = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    res += decoder.decode(value)
  }
  return JSON.parse(res)
}

const dateObjToRead = (datestring: string): string => {
  try {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    const date = new Date(datestring)
    return `${date.getDate()}-${monthNames[date.getMonth()]}-${date.getFullYear()}` // DD-MM-YYYY
  } catch (err) {
    return 'N/A'
  }
}

const getColor = (value: number, maxValue: number): string => {
  try {
    const ratio = value / maxValue
    let r = 0, g = 0, b = 0
    
    if (ratio <= 0.33) {
      // Transition Blue (0,0,255) → Green (0,255,0)
      const t = ratio / 0.33 // Normalize to [0,1] within this range
      r = 0
      g = Math.round(255 * t)
      b = Math.round(255 * (1 - t))
    } else if (ratio <= 0.66) {
      // Transition Green (0,255,0) → Yellow (255,255,0)
      const t = (ratio - 0.33) / 0.33 // Normalize to [0,1] within this range
      r = Math.round(255 * t)
      g = 255
      b = 0
    } else {
      // Transition Yellow (255,255,0) → Red (255,0,0)
      const t = (ratio - 0.66) / 0.34 // Normalize to [0,1] within this range
      r = 255
      g = Math.round(255 * (1 - t))
      b = 0
    }
  
    return `rgb(${r}, ${g}, ${b})`
  } catch (err) {
    console.log(err)
    return defaultMarkerColor
  }
}

const generateColorGradient = (maxValue: number): Record<number, string> => {
  const gradient: Record<number, string> = {}
  for (let i = 0; i <= maxValue; i++)
    gradient[i] = getColor(i, maxValue)
  return gradient
}

const executePeriodically = (
  times: number,
  interval: number,
  callback: (...params: any[]) => boolean | void,
  ...params: any[]
): void => {
  let count = 0
  let timeoutId: NodeJS.Timeout | undefined

  const execute = () => {
    if (count < times) {
      const shouldContinue = callback(...params)
      if (!shouldContinue) {
        clearTimeout(timeoutId)
        return
      }
      count++
      timeoutId = setTimeout(execute, interval)
    } else clearTimeout(timeoutId)
  }
  execute()
}

const naturalCompare = (a: string, b: string): number => {
  const splitRegex = /(\d+|\D+)/g
  const aParts = a.split(splitRegex).filter(Boolean)
  const bParts = b.split(splitRegex).filter(Boolean)
  
  for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
    const aNum = parseInt(aParts[i])
    const bNum = parseInt(bParts[i])
    
    if (!isNaN(aNum) && !isNaN(bNum)) {
      if (aNum !== bNum) return aNum - bNum
    } else if (aParts[i] !== bParts[i]) {
      return aParts[i].localeCompare(bParts[i])
    }
  }
  
  return aParts.length - bParts.length
}

interface NamedItem {
  name: string
  [key: string]: any
}

const sortListAlphaNumeric = <T extends NamedItem>(data: T[], asc: boolean = true): T[] => {
  const quickSort = (arr: T[], low: number, high: number): void => {
    const pivot = arr[Math.floor((low + high) / 2)].name.toLowerCase()
    let i = low, j = high
    while (i <= j) {
      if (asc) {
        while (naturalCompare(arr[i].name.toLowerCase(), pivot) < 0) i++
        while (naturalCompare(arr[j].name.toLowerCase(), pivot) > 0) j--
      } else {
        while (naturalCompare(arr[i].name.toLowerCase(), pivot) > 0) i++
        while (naturalCompare(arr[j].name.toLowerCase(), pivot) < 0) j--
      }
      if (i <= j) {
        [arr[i], arr[j]] = [arr[j], arr[i]]
        i++
        j--
      }
    }
    if (low < j) quickSort(arr, low, j)
    if (i < high) quickSort(arr, i, high)
  }

  quickSort(data, 0, data.length - 1)

  return data
}

const hasValidExtension = (filename: string): boolean => {
  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) return false
  const ext = filename.slice(lastDotIndex).toLowerCase()
  return extensions.has(ext)
}

const sleepForSec = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const hexToRgb = (hex: string): [number, number, number, number] => {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16) || 0;
  const g = parseInt(cleanHex.substring(2, 4), 16) || 0;
  const b = parseInt(cleanHex.substring(4, 6), 16) || 0;
  return [r, g, b, 150]; // 150 is the alpha/opacity (0-255)
}

export {
  debounce,
  hexToIColor,
  decodeRes,
  dateObjToRead,
  generateColorGradient,
  executePeriodically,
  naturalCompare,
  sortListAlphaNumeric,
  hasValidExtension,
  sleepForSec,
  hexToRgb
}